// Fonctions pures de calcul des KPIs cabinet (F-05).
// Aucune dépendance React/Supabase. Bornes en heure locale.

export interface DateRange {
  start: Date;
  end: Date; // exclusif
}

/** [1er du mois 00:00, 1er du mois suivant 00:00) en heure locale. */
export function monthRange(now: Date): DateRange {
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
  return { start, end };
}

/** [lundi 00:00, lundi suivant 00:00) en heure locale. Semaine ISO. */
export function weekRange(now: Date): DateRange {
  const day = now.getDay(); // 0=dim … 6=sam
  const diffToMonday = (day + 6) % 7; // dim→6, lun→0, mar→1 …
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - diffToMonday,
    0, 0, 0, 0,
  );
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7, 0, 0, 0, 0);
  return { start, end };
}

/** true si `iso` (date string) ∈ [range.start, range.end). null/invalide → false. */
export function inRange(iso: string | null | undefined, range: DateRange): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return t >= range.start.getTime() && t < range.end.getTime();
}

export interface KpiDossierInput {
  opened_at: string;
  archived_at: string | null;
}

export interface KpiBillingInput {
  ht_total: number;
  status: string;
  issue_date: string | null;
  paid_at: string | null;
}

export interface KpiTimeInput {
  duration_minutes: number;
  is_billable: boolean;
  started_at: string;
}

export interface CabinetKpis {
  dossiersOpened: number;
  dossiersArchived: number;
  revenueHt: number;
  revenuePaidHt: number;
  billableMinutesWeek: number;
  overdueHt: number;
  overdueCount: number;
}

export interface ComputeKpisArgs {
  dossiers: KpiDossierInput[];
  billing: KpiBillingInput[];
  time: KpiTimeInput[];
  now: Date;
}

/** Une facture compte dans le CA si elle est réellement émise. */
const ISSUED_STATUSES = new Set(['sent', 'paid', 'overdue']);

export function computeCabinetKpis(args: ComputeKpisArgs): CabinetKpis {
  const { dossiers, billing, time, now } = args;
  const month = monthRange(now);
  const week = weekRange(now);

  let dossiersOpened = 0;
  let dossiersArchived = 0;
  for (const d of dossiers) {
    if (inRange(d.opened_at, month)) dossiersOpened++;
    if (inRange(d.archived_at, month)) dossiersArchived++;
  }

  let revenueHt = 0;
  let revenuePaidHt = 0;
  let overdueHt = 0;
  let overdueCount = 0;
  for (const b of billing) {
    if (ISSUED_STATUSES.has(b.status) && inRange(b.issue_date, month)) {
      revenueHt += b.ht_total;
    }
    if (b.status === 'paid' && inRange(b.paid_at, month)) {
      revenuePaidHt += b.ht_total;
    }
    if (b.status === 'overdue') {
      overdueHt += b.ht_total;
      overdueCount++;
    }
  }

  let billableMinutesWeek = 0;
  for (const t of time) {
    if (t.is_billable && inRange(t.started_at, week)) {
      billableMinutesWeek += t.duration_minutes;
    }
  }

  return {
    dossiersOpened,
    dossiersArchived,
    revenueHt,
    revenuePaidHt,
    billableMinutesWeek,
    overdueHt,
    overdueCount,
  };
}

export interface WeeklySeries {
  /** 4 valeurs, index 0 = semaine la plus ancienne (S-3), index 3 = courante (S0). */
  revenueHt: number[];
  billableMinutes: number[];
}

export interface ComputeSeriesArgs {
  billing: KpiBillingInput[];
  time: KpiTimeInput[];
  now: Date;
}

const WEEKS = 4;
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Builds 4-week sparkline series (S-3…S0). Prior-week boundaries are derived by
 * subtracting fixed-ms offsets from the calendar-correct current week start.
 * This gives a ±1h drift on bucket boundaries at DST transitions, which is
 * acceptable per spec — weekly totals across all 4 buckets remain exact.
 */
export function computeWeeklySeries(args: ComputeSeriesArgs): WeeklySeries {
  const { billing, time, now } = args;
  const current = weekRange(now);

  const ranges: DateRange[] = [];
  for (let i = WEEKS - 1; i >= 0; i--) {
    const start = new Date(current.start.getTime() - i * MS_PER_WEEK);
    const end = new Date(start.getTime() + MS_PER_WEEK);
    ranges.push({ start, end });
  }

  const revenueHt = new Array<number>(WEEKS).fill(0);
  const billableMinutes = new Array<number>(WEEKS).fill(0);

  ranges.forEach((r, i) => {
    let rev = 0;
    let bill = 0;
    for (const b of billing) {
      if (ISSUED_STATUSES.has(b.status) && inRange(b.issue_date, r)) {
        rev += b.ht_total;
      }
    }
    for (const t of time) {
      if (t.is_billable && inRange(t.started_at, r)) {
        bill += t.duration_minutes;
      }
    }
    revenueHt[i] = rev;
    billableMinutes[i] = bill;
  });

  return { revenueHt, billableMinutes };
}
